/**
 * LoyaltyDashboard - Customer loyalty program dashboard
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { 
  Star, Gift, Trophy, Users, Copy, CheckCircle, 
  Crown, Award, Sparkles, ArrowRight, Loader2,
  TrendingUp, History
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = {
  bronze: Award,
  silver: Star,
  gold: Crown,
  platinum: Sparkles
};

const TIER_COLORS = {
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  silver: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-400' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-400' }
};

export default function LoyaltyDashboard({ userEmail }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);

  useEffect(() => {
    if (userEmail) {
      fetchDashboard();
      fetchRewards();
    }
  }, [userEmail]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/loyalty/events/dashboard/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch loyalty dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/loyalty/events/rewards/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        setRewards(data.rewards || []);
      }
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplyingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/loyalty/events/apply-referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_user_email: userEmail,
          referral_code: referralCode.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setReferralDialogOpen(false);
        setReferralCode('');
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to apply referral code');
      }
    } catch (error) {
      toast.error('Failed to apply referral code');
    } finally {
      setApplyingCode(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(dashboard?.account?.referral_code || '');
    toast.success('Referral code copied!');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </CardContent>
      </Card>
    );
  }

  if (!dashboard) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <p>Unable to load loyalty program. Please try again.</p>
        </CardContent>
      </Card>
    );
  }

  const { account, tier_info, next_tier, points_to_next_tier, recent_transactions } = dashboard;
  const currentTier = account?.tier?.toLowerCase() || 'bronze';
  const TierIcon = TIER_ICONS[currentTier] || Award;
  const tierColor = TIER_COLORS[currentTier] || TIER_COLORS.bronze;

  // Calculate progress to next tier
  const progressPercent = next_tier 
    ? ((tier_info?.min_points || 0) / (next_tier?.min_points || 1)) * 100
    : 100;

  return (
    <div className="space-y-6">
      {/* Tier Card */}
      <Card className={`border-2 ${tierColor.border}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${tierColor.bg}`}>
                <TierIcon className={`w-8 h-8 ${tierColor.text}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Your Tier</p>
                <h2 className={`text-2xl font-bold capitalize ${tierColor.text}`}>
                  {currentTier}
                </h2>
                <p className="text-lg font-semibold">
                  {account?.available_points?.toLocaleString() || 0} Points
                </p>
              </div>
            </div>
            
            {tier_info?.discount_pct > 0 && (
              <Badge className={`${tierColor.bg} ${tierColor.text} text-lg px-4 py-2`}>
                {tier_info.discount_pct}% OFF
              </Badge>
            )}
          </div>

          {/* Progress to next tier */}
          {next_tier && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="capitalize">{currentTier}</span>
                <span className="capitalize text-gray-500">
                  {points_to_next_tier?.toLocaleString()} points to {next_tier.name}
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          )}

          {/* Benefits */}
          {tier_info?.benefits && (
            <div className="mt-4 flex flex-wrap gap-2">
              {tier_info.benefits.map((benefit, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                  {benefit}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{account?.total_bookings || 0}</p>
            <p className="text-xs text-gray-500">Total Bookings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{account?.referral_count || 0}</p>
            <p className="text-xs text-gray-500">Referrals</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Gift className="w-6 h-6 mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{rewards?.filter(r => r.available).length || 0}</p>
            <p className="text-xs text-gray-500">Available Rewards</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Refer & Earn
          </CardTitle>
          <CardDescription>
            Share your code and earn 500 points for each friend who joins!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3 font-mono text-lg font-bold text-center">
              {account?.referral_code || 'Loading...'}
            </div>
            <Button onClick={copyReferralCode} variant="outline">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="mt-4 flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setReferralDialogOpen(true)}
            >
              Have a code? Apply it
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Available Rewards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-500" />
            Available Rewards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {rewards.map((reward) => (
              <div 
                key={reward.id}
                className={`p-4 rounded-lg border ${
                  reward.available 
                    ? 'border-purple-200 bg-purple-50 cursor-pointer hover:bg-purple-100' 
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
                onClick={() => reward.available && setSelectedReward(reward)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{reward.name}</span>
                  {reward.available && (
                    <Badge className="bg-green-100 text-green-700 text-xs">Available</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {reward.points_required.toLocaleString()} points
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {recent_transactions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recent_transactions.map((txn, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{txn.reason}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`font-bold ${
                    txn.points > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {txn.points > 0 ? '+' : ''}{txn.points}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Apply Referral Dialog */}
      <Dialog open={referralDialogOpen} onOpenChange={setReferralDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Referral Code</DialogTitle>
            <DialogDescription>
              Enter a referral code to get 10% off your first booking!
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Enter referral code (e.g., REF-ABC12345)"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            className="font-mono"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReferralDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyReferral} disabled={applyingCode}>
              {applyingCode && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
